/**
 * CommandPalette — « Aller à… » (Ctrl+K) on the one Combobox
 * (design/ux/01_foundations.md §6.5, WP-17; AUD-NAV-06, AUD-NAV-20,
 * AUD-SRCH-11, OP-F-16).
 *
 *  · A dialog holding an inline Combobox; focus lands in the input.
 *  · Empty query: « Récents » (the last pages this user visited). Typing:
 *    « Pages » — every page the role's menu shows, by its full path,
 *    accent-insensitive — then « Produits » after 2 characters through the
 *    product search endpoint (the WP-13 engine sits behind it), with
 *    « Recherche des produits… » while it loads (never « Aucun résultat »).
 *  · ↑ ↓ move, Entrée opens, Ctrl+Entrée opens in a new tab, Échap closes;
 *    the footer hints are these keys.
 *
 *  · « Actions » (Trésorerie UX 01 §2.6, RC-132): the command verbs the role
 *    may reach (« Nouvel encaissement »…) — typed queries list them before the
 *    pages; each opens its form on the current page when it hosts it.
 *  · « Lots » (refonte WP-37 / cross-module XWP-12, OP-PSA-33): a query that
 *    IS a N° lot or a lot label lists that lot (product, expiry, sellable
 *    units); Entrée opens the Stock & lots list narrowed on it. Best effort —
 *    its failure never hides the products.
 *
 * Props: `open`, `onClose`, `pages` (the role's index), `verbs` (its command
 * verbs, `buildVerbIndex`), `recents`,
 * `managesCatalogue` (product hits open the product; else the product search
 * page), `canSearchProducts`, `canSearchLots` (stock:read).
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Kbd, Modal } from "@/components/ui/primitives";
import { Combobox, type ComboboxOption } from "@/components/ui/Combobox";
import {
  PALETTE_GROUPS,
  lotMeta,
  lotTarget,
  lotsCorrespondants,
  matchPages,
  productTarget,
  type PalettePage,
} from "@/lib/command-palette.js";
import { produitApi, stockApi } from "@/services/index.js";
import {
  PALETTE_LOT_FETCH,
  PALETTE_LOT_LIMIT,
  PALETTE_PAGE_LIMIT,
  PALETTE_PRODUCT_LIMIT,
  PALETTE_VERB_LIMIT,
} from "@/config/ui.config.js";

interface PaletteOption extends ComboboxOption {
  kind: "page" | "verbe" | "produit" | "lot";
  href: string;
  /** A verb on the current page replaces the history entry (Back never lands on the same page). */
  replace?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  pages: ReadonlyArray<PalettePage>;
  verbs?: ReadonlyArray<PalettePage & { replace?: boolean }>;
  recents: ReadonlyArray<PalettePage>;
  managesCatalogue: boolean;
  canSearchProducts: boolean;
  canSearchLots?: boolean;
}

const toPageOption = (p: PalettePage, group: string): PaletteOption => ({
  value: `page:${p.value}`,
  label: p.label,
  meta: p.meta,
  keywords: p.keywords,
  group,
  kind: "page",
  href: p.value,
});

/** A reference field that may arrive as text or as a related row ({ nom } / { libelle }). */
function refText(v: any): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "object") return v.nom ?? v.libelle ?? v.label ?? undefined;
  return String(v);
}

function productMeta(p: any): string {
  return [refText(p?.dci), refText(p?.forme), refText(p?.dosage)].filter(Boolean).join(" · ");
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
  pages,
  verbs = [],
  recents,
  managesCatalogue,
  canSearchProducts,
  canSearchLots = false,
}) => {
  const navigate = useNavigate();
  const recentOptions = React.useMemo(
    () => recents.map((p) => toPageOption(p, PALETTE_GROUPS.RECENTS)),
    [recents],
  );
  const pageOptions = React.useMemo(() => pages.map((p) => toPageOption(p, PALETTE_GROUPS.PAGES)), [pages]);
  const all = React.useMemo(() => [...recentOptions, ...pageOptions], [recentOptions, pageOptions]);

  const filterOptions = React.useCallback(
    (_: PaletteOption[], query: string): PaletteOption[] => {
      if (!query.trim()) return recentOptions;
      const actions = matchPages(verbs as PalettePage[], query, PALETTE_VERB_LIMIT).map(
        (v: PalettePage & { replace?: boolean }) => ({
          ...toPageOption(v, PALETTE_GROUPS.ACTIONS),
          value: `verbe:${v.value}`,
          kind: "verbe" as const,
          replace: Boolean(v.replace),
        }),
      );
      return [
        ...actions,
        ...matchPages(pages as PalettePage[], query, PALETTE_PAGE_LIMIT).map((p: PalettePage) =>
          toPageOption(p, PALETTE_GROUPS.PAGES),
        ),
      ];
    },
    [pages, verbs, recentOptions],
  );

  const loadProducts = React.useCallback(
    async (query: string, ctx: { signal?: AbortSignal }): Promise<PaletteOption[]> => {
      const r: any = await produitApi.search(query.trim(), PALETTE_PRODUCT_LIMIT, { signal: ctx.signal });
      const rows: any[] = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
      return rows.slice(0, PALETTE_PRODUCT_LIMIT).map((p) => {
        const label = String(p.nom_commercial ?? "");
        return {
          value: `produit:${p.id}`,
          label,
          meta: productMeta(p),
          group: PALETTE_GROUPS.PRODUITS,
          kind: "produit",
          href: productTarget({ id: String(p.id), label }, managesCatalogue),
        };
      });
    },
    [managesCatalogue],
  );

  const loadLots = React.useCallback(
    async (query: string, ctx: { signal?: AbortSignal }): Promise<PaletteOption[]> => {
      const r: any = await stockApi.listLots({ search: query.trim(), limit: PALETTE_LOT_FETCH, inclure_epuises: true }, { signal: ctx.signal });
      const rows: any[] = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : [];
      return lotsCorrespondants(rows, query, PALETTE_LOT_LIMIT).map((l: any) => ({
        value: `lot:${l.id}`,
        label: `Lot ${String(l.numero_lot ?? "").trim()}`,
        meta: lotMeta(l),
        group: PALETTE_GROUPS.LOTS,
        kind: "lot",
        href: lotTarget(l),
      }));
    },
    [],
  );

  // Produits, then Lots; the lot read is best effort (its failure never hides the products).
  const loadRemote = React.useCallback(
    async (query: string, ctx: { signal?: AbortSignal }): Promise<PaletteOption[]> => {
      const [produits, lots] = await Promise.all([
        canSearchProducts ? loadProducts(query, ctx) : Promise.resolve([] as PaletteOption[]),
        canSearchLots
          ? loadLots(query, ctx).catch((e) => {
              if (ctx.signal?.aborted) throw e;
              return [] as PaletteOption[];
            })
          : Promise.resolve([] as PaletteOption[]),
      ]);
      return [...produits, ...lots];
    },
    [canSearchProducts, canSearchLots, loadProducts, loadLots],
  );

  const go = (o: PaletteOption | null) => {
    if (!o) return;
    onClose();
    navigate(o.href, o.replace ? { replace: true } : undefined);
  };
  const goNewTab = (o: PaletteOption) => {
    window.open(o.href, "_blank", "noopener");
  };

  return (
    <Modal open={open} onClose={onClose} dirty={false} size="md" title="Aller à…">
      {/* F-keys typed here never reach a legacy page listener underneath. */}
      <div
        onKeyDown={(e) => {
          if (/^F([2-9]|1[0-2])$/.test(e.key)) e.stopPropagation();
        }}
      >
        <Combobox<PaletteOption>
          inline
          autoActivateFirst
          autoFocus
          size="lg"
          label="Rechercher une page ou un produit"
          placeholder="Page, produit…"
          options={all}
          filterOptions={filterOptions}
          loadOptions={canSearchProducts || canSearchLots ? loadRemote : undefined}
          pendingText="Recherche des produits…"
          onChange={go}
          onPickAlt={goNewTab}
          emptyText={(q) =>
            q
              ? `Aucune page ni aucun produit ne correspond à « ${q} »`
              : "Aucune page récente : tapez le nom d'une page ou d'un produit."
          }
          emptyHint="La recherche ignore les accents et l'ordre des mots."
        />
        <div className="pk-palette-hints" aria-hidden="true">
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> choisir
          </span>
          <span>
            <Kbd>Entrée</Kbd> ouvrir
          </span>
          <span>
            <Kbd>Ctrl + Entrée</Kbd> nouvel onglet
          </span>
          <span>
            <Kbd>Échap</Kbd> fermer
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default CommandPalette;
